import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { useEffect, useRef, useState } from "react";
import { ReactSVG } from "react-svg";
import styles from "./Select.module.scss";

export type SelectOption = {
  value: string;
  label: string;
};

type SelectProps = {
  options: SelectOption[];
  onSelect: (value: string) => void;
  preSelectedOption?: SelectOption;
  classNames?: {
    wrapper?: string;
    button?: string;
    dropdown?: string;
  };
};

export const Select = ({
  onSelect,
  options,
  preSelectedOption,
  classNames,
}: SelectProps) => {
  const [selectedOptions, setSelectedOption] = useState(
    preSelectedOption ?? null,
  );
  const [isOpen, setIsOpen] = useState(false);
  const { getIcon } = useGlobalContext();
  const selectRef = useRef<HTMLDivElement>(null);

  const handleOptionClick = (option: SelectOption) => {
    setSelectedOption(option);
    onSelect(option.value);
    setIsOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      selectRef.current &&
      !selectRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      className={`${styles["select-wrapper"]} ${classNames?.wrapper}`}
      ref={selectRef}
    >
      <button
        className={classNames?.button}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <p>{selectedOptions?.label ?? "-"}</p>
        <ReactSVG
          className={`${styles["dropdown-icon"]} ${isOpen ? styles["dropdown-open"] : ""}`}
          src={getIcon("altArrowRight")}
          width={13}
          height={13}
          wrapper="svg"
          aria-hidden
        />
      </button>

      {isOpen && (
        <ul
          className={`${styles["dropdown-list"]} ${classNames?.dropdown}`}
          role="listbox"
          aria-activedescendant={
            selectedOptions ? `option-${selectedOptions.value}` : undefined
          }
          tabIndex={-1}
        >
          {options.map((option) => (
            <li
              className={`${selectedOptions?.value === option.value ? styles["is-selected"] : ""}`}
              key={option.value}
              id={`option-${option.value}`}
              role="option"
              aria-selected={selectedOptions?.value === option.value}
              onClick={() => handleOptionClick(option)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOptionClick(option);
                }
              }}
              tabIndex={0}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
